// @flow
import React, { PureComponent } from 'react';

import isNil from 'lodash/fp/isNil';
import type { Bloc, BlocContent, Attributes } from 'types/NewsletterBloc';
import TagManager from 'react-gtm-module';
import BackgroundImage from 'containers/fragments/BackgroundImageContainer';
import { dynamicClassName } from 'utils/dynamicClassName';
import SimpleCarousel from 'components/fragments/SimpleCarousel/SimpleCarousel';

import './HTMLGallery.css';

export type Props = {
  childrens: Array<Bloc | BlocContent>,
  attributes: Array<Attributes>,
  tagName: string
};

export type ComponentState = {
  focus: boolean,
  focusImg: number,
  caroussel: boolean
};

class HTMLGallery extends PureComponent<Props, ComponentState> {
  gallery: ?HTMLElement;
  state: ComponentState = {
    focus: false,
    focusImg: 0,
    caroussel: false
  };

  handleClick = (id: number) => {
    return () => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'eventGA',
          eventCategory: 'module gallerie',
          eventAction: 'clic photo',
          eventLabel: window.location.pathname
        },
        dataLayerName: 'dataLayer'
      });
      this.setState({
        focusImg: id
      });
    };
  };

  componentDidMount() {
    window.addEventListener('keyup', this.handleKeyUp, false);
    document.addEventListener('click', this.handleInsideClick, false);
  }

  componentWillUnmount() {
    window.removeEventListener('keyup', this.handleKeyUp, false);
    document.removeEventListener('click', this.handleInsideClick, false);
  }

  handleInsideClick = (e: Event) => {
    if (!isNil(this.gallery) && this.gallery) {
      if (e.target instanceof HTMLElement) {
        if (!this.gallery.contains(e.target)) {
          this.setState({ focus: false });
        } else {
          this.setState({ focus: true });
        }
      }
    }
  };

  handleKeyUp = (e: KeyboardEvent) => {
    const keys = {
      '13': () => {
        e.preventDefault();
        this.openCaroussel();
      },
      '37': () => {
        e.preventDefault();
        this._handleClickLeft();
      },
      '39': () => {
        e.preventDefault();
        this._handleClickRight();
      }
    };

    if (this.state.focus === true && keys[e.keyCode]) {
      keys[e.keyCode]();
    }
  };

  _handleClickRight = () => {
    const { childrens } = this.props;
    const { focusImg } = this.state;

    if (childrens.length - 1 <= focusImg) {
      this.setState({
        focusImg: 0
      });
    } else {
      this.setState(prevState => ({
        focusImg: prevState.focusImg + 1
      }));
    }
  };

  _handleClickLeft = () => {
    const { childrens } = this.props;
    const { focusImg } = this.state;

    if (focusImg <= 0) {
      this.setState({
        focusImg: childrens.length - 1
      });
    } else {
      this.setState(prevState => ({
        focusImg: prevState.focusImg - 1
      }));
    }
  };

  renderBigImg = (childrens: Array<Bloc | BlocContent>) => {
    const { focusImg } = this.state;

    if (childrens[focusImg]) {
      if (childrens[focusImg].featured_image) {
        return (
          <figure onClick={this.openCaroussel} role="button" tabIndex={0}>
            <img alt="" src={childrens[focusImg].featured_image.src} />
            <figcaption>{childrens[focusImg].figcaption && childrens[focusImg].figcaption}</figcaption>
          </figure>
        );
      }
    }
    return null;
  };

  openCaroussel = () => {
    this.setState({
      caroussel: true
    });
  };

  closeCaroussel = () => {
    this.setState({
      caroussel: false
    });
  };

  renderListImg = (childrens: Array<Bloc | BlocContent>): Array<?React$Element<any>> => {
    const { focusImg } = this.state;

    return childrens.map((blocFigure: Bloc | BlocContent, index) => {
      if (blocFigure.featured_image) {
        const pictureClassName = dynamicClassName('picture');
        if (index === focusImg) {
          pictureClassName.add('picture--active');
        }
        return (
          <div className={pictureClassName.build()} key={index} onClick={this.handleClick(index)} role="button" tabIndex={0}>
            <BackgroundImage
              className="picture__thumbnail"
              illustration={blocFigure.featured_image?.src || ''}
              srcset={blocFigure.featured_image?.sizes || undefined}
            />
          </div>
        );
      }
      return null;
    });
  };

  render() {
    const { caroussel, focusImg } = this.state;
    const { childrens, attributes } = this.props;

    let listAttributes = {};
    for (const attr of attributes) {
      if (attr.name && attr.name === 'id') {
        listAttributes[attr.name] = attr.value;
      }
    }
    let childGallery = childrens;
    if (childrens.length > 0 && childrens[0].tagName === 'gallery' && childrens[0].children) {
      childGallery = childrens[0].children;
    }

    return (
      <section className="container--full carousel-photo" {...listAttributes}>
        {caroussel && <SimpleCarousel onCloseRequest={this.closeCaroussel} data={childGallery} initialIndex={focusImg} />}
        {this.renderBigImg(childGallery)}
        <div className="news-slider" ref={r => (this.gallery = r)}>
          <div className="news-slider__wrapper news-slider__wrapper--picture">{this.renderListImg(childGallery)}</div>
        </div>
      </section>
    );
  }
}

export default HTMLGallery;
